import { io, type Socket } from "socket.io-client";
import type { PromotionPiece } from "../game/engine";
import type { Color, GameState, Square } from "../game/types";

/**
 * Client Socket.IO du serveur d'arbitrage `dkg-multi-game-server`.
 *
 * Les interfaces ci-dessous sont une copie fidele (et volontaire) du protocole
 * decrit dans `dkg-multi-game-server/src/protocol/events.ts` : ce depot ne
 * dependant pas du code du serveur, le contrat est duplique ici. Toute
 * evolution du protocole doit etre repercutee dans ce fichier.
 */

export const DEFAULT_SERVER_URL = "http://localhost:4000";

/** URL du serveur multijoueur : surchargeable au build via `VITE_MULTI_SERVER_URL` (cf. .env.example). */
export function resolveServerUrl(): string {
  const fromEnv = import.meta.env.VITE_MULTI_SERVER_URL;
  return typeof fromEnv === "string" && fromEnv.trim() !== "" ? fromEnv.trim() : DEFAULT_SERVER_URL;
}

// ---------------------------------------------------------------------------
// Protocole (duplique de dkg-multi-game-server/src/protocol/events.ts)
// ---------------------------------------------------------------------------

export type SessionStatus = "lobby" | "playing" | "finished";

export interface PublicSessionPlayer {
  id: string;
  name: string;
  ready: boolean;
  connected: boolean;
}

export interface PublicSession {
  code: string;
  gameId: string;
  status: SessionStatus;
  players: PublicSessionPlayer[];
  spectatorCount: number;
}

export type AckResult<T = Record<never, never>> = ({ ok: true } & T) | { ok: false; error: string };

export interface JoinedSessionResult {
  session: PublicSession;
  selfId: string;
  /** `null` pour un spectateur (pas de reconnexion possible). */
  token: string | null;
}

export interface GameOverPayload {
  winnerIds: string[] | null;
  /** "checkmate" | "stalemate" | "draw-*" | "forfeit" */
  reason: string;
}

export interface PlayerDisconnectedPayload {
  playerId: string;
  name: string;
  graceMs: number;
}

export interface PlayerReconnectedPayload {
  playerId: string;
  name: string;
}

/** Intention de coup pour les echecs (cf. `ChessAdapter.applyIntent`). */
export interface ChessMoveIntent {
  type: "move";
  from: Square;
  to: Square;
  promotion?: PromotionPiece;
}

/** Entree d'historique serialisee par le serveur : `move` est directement rejouable par `ChessEngine.move`. */
export interface ChessRemoteHistoryEntry {
  san: string;
  move: { from: Square; to: Square; promotion?: PromotionPiece };
}

/** Etat canonique renvoye par `ChessAdapter.serializeStateFor` (le champ `board` porte tout le `GameState`). */
export interface ChessRemoteState {
  board: GameState;
  turn: Color;
  status: string;
  history: ChessRemoteHistoryEntry[];
  colorOf: Record<string, Color>;
}

export interface OnlineServerEvents {
  "session:update": (session: PublicSession) => void;
  "game:state": (state: ChessRemoteState) => void;
  "game:over": (result: GameOverPayload) => void;
  "player:disconnected": (info: PlayerDisconnectedPayload) => void;
  "player:reconnected": (info: PlayerReconnectedPayload) => void;
}

interface OnlineClientEvents {
  "session:create": (
    payload: { gameId: string; playerName: string },
    ack: (result: AckResult<JoinedSessionResult>) => void,
  ) => void;
  "session:join": (
    payload: { code: string; playerName: string },
    ack: (result: AckResult<JoinedSessionResult>) => void,
  ) => void;
  "session:spectate": (
    payload: { code: string; name: string },
    ack: (result: AckResult<JoinedSessionResult>) => void,
  ) => void;
  "session:reconnect": (
    payload: { code: string; token: string },
    ack: (result: AckResult<JoinedSessionResult>) => void,
  ) => void;
  "session:leave": (payload: { code: string; token: string }, ack: (result: AckResult) => void) => void;
  "player:ready": (payload: { code: string; token: string; ready: boolean }, ack: (result: AckResult) => void) => void;
  "game:intent": (
    payload: { code: string; token: string; intent: ChessMoveIntent },
    ack: (result: AckResult<{ state: ChessRemoteState }>) => void,
  ) => void;
  "game:resign": (payload: { code: string; token: string }, ack: (result: AckResult) => void) => void;
}

type OnlineSocket = Socket<OnlineServerEvents, OnlineClientEvents>;

/** Erreur "metier" renvoyee par le serveur (`{ok:false, error}`) ou par le transport. */
export class OnlineError extends Error {}

const SERVER_EVENTS = [
  "session:update",
  "game:state",
  "game:over",
  "player:disconnected",
  "player:reconnected",
] as const satisfies readonly (keyof OnlineServerEvents)[];

/** Delai max d'attente d'un accuse de reception avant d'abandonner l'appel. */
const ACK_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Persistance de la session (reprise apres refresh / perte de connexion)
// ---------------------------------------------------------------------------

const STORAGE_KEY = "dkg-chess-online-session";

export interface StoredOnlineSession {
  code: string;
  token: string;
  playerName: string;
}

export function loadStoredSession(): StoredOnlineSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredOnlineSession>;
    if (typeof parsed?.code !== "string" || typeof parsed?.token !== "string") return null;
    return { code: parsed.code, token: parsed.token, playerName: parsed.playerName ?? "" };
  } catch {
    return null;
  }
}

export function saveStoredSession(session: StoredOnlineSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* stockage indisponible (navigation privee) : la reprise sera simplement impossible */
  }
}

export function clearStoredSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* idem */
  }
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

/**
 * Enveloppe typee et "promisifiee" autour de `socket.io-client` : chaque
 * evenement client -> serveur devient une methode async qui rejette avec une
 * `OnlineError` si le serveur repond `{ok:false}`.
 *
 * Le client memorise `code`/`token` des la premiere entree en session et
 * rejoue automatiquement `session:reconnect` a chaque reconnexion du socket
 * (le serveur, lui, ne reconnait un joueur que par son jeton).
 */
export class OnlineClient {
  readonly socket: OnlineSocket;

  private code: string | null = null;
  private token: string | null = null;
  private everJoined = false;

  /** Derniers messages recus, mis en cache pour les consommateurs branches apres coup. */
  private lastSession: PublicSession | null = null;
  private lastState: ChessRemoteState | null = null;

  private readonly listeners = new Map<keyof OnlineServerEvents, Set<(payload: never) => void>>();
  private readonly connectionListeners = new Set<(online: boolean) => void>();

  constructor(url: string = resolveServerUrl()) {
    this.socket = io(url, { transports: ["websocket", "polling"], autoConnect: false });

    for (const event of SERVER_EVENTS) {
      this.socket.on(event, ((payload: unknown) => {
        if (event === "session:update") this.lastSession = payload as PublicSession;
        if (event === "game:state") this.lastState = payload as ChessRemoteState;
        this.emitLocal(event, payload);
      }) as never);
    }

    this.socket.on("connect", () => {
      this.notifyConnection(true);
      // Reconnexion du transport : le serveur a oublie ce socket, il faut lui
      // reassocier le joueur via son jeton (fenetre de grace de 3 min cote serveur).
      if (this.everJoined && this.code && this.token) {
        void this.reconnect(this.code, this.token).catch(() => {
          /* session expiree/terminee : le consommateur le verra a l'absence d'evenements */
        });
      }
    });
    this.socket.on("disconnect", () => this.notifyConnection(false));
  }

  get sessionCode(): string | null {
    return this.code;
  }

  get sessionToken(): string | null {
    return this.token;
  }

  get cachedSession(): PublicSession | null {
    return this.lastSession;
  }

  get cachedState(): ChessRemoteState | null {
    return this.lastState;
  }

  get connected(): boolean {
    return this.socket.connected;
  }

  /** Ouvre la connexion (ou resout immediatement si deja connectee). */
  connect(): Promise<void> {
    if (this.socket.connected) return Promise.resolve();
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new OnlineError("Serveur injoignable (delai depasse)."));
      }, ACK_TIMEOUT_MS);
      const cleanup = () => {
        window.clearTimeout(timer);
        this.socket.off("connect", onConnect);
        this.socket.off("connect_error", onError);
      };
      const onConnect = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };
      const onError = (err: Error) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new OnlineError(`Connexion au serveur impossible : ${err.message}`));
      };
      this.socket.once("connect", onConnect);
      this.socket.once("connect_error", onError);
      this.socket.connect();
    });
  }

  // --- Evenements serveur -> client -------------------------------------------------

  on<K extends keyof OnlineServerEvents>(event: K, cb: OnlineServerEvents[K]): void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb as (payload: never) => void);
  }

  off<K extends keyof OnlineServerEvents>(event: K, cb: OnlineServerEvents[K]): void {
    this.listeners.get(event)?.delete(cb as (payload: never) => void);
  }

  /** Notifie des pertes/retours de la connexion socket elle-meme (independamment du protocole de jeu). */
  onConnectionChange(cb: (online: boolean) => void): () => void {
    this.connectionListeners.add(cb);
    return () => this.connectionListeners.delete(cb);
  }

  private emitLocal(event: keyof OnlineServerEvents, payload: unknown): void {
    for (const cb of this.listeners.get(event) ?? []) {
      try {
        (cb as (p: unknown) => void)(payload);
      } catch (err) {
        console.error(`[online] listener ${event}`, err);
      }
    }
  }

  private notifyConnection(online: boolean): void {
    for (const cb of this.connectionListeners) {
      try {
        cb(online);
      } catch (err) {
        console.error("[online] listener connexion", err);
      }
    }
  }

  // --- Evenements client -> serveur -------------------------------------------------

  async createSession(playerName: string): Promise<JoinedSessionResult> {
    const result = await this.request("session:create", { gameId: "chess", playerName });
    this.rememberSession(result);
    return result;
  }

  async joinSession(code: string, playerName: string): Promise<JoinedSessionResult> {
    const result = await this.request("session:join", { code: normalizeCode(code), playerName });
    this.rememberSession(result);
    return result;
  }

  async spectateSession(code: string, name: string): Promise<JoinedSessionResult> {
    const result = await this.request("session:spectate", { code: normalizeCode(code), name });
    this.rememberSession(result);
    return result;
  }

  async reconnect(code: string, token: string): Promise<JoinedSessionResult> {
    const result = await this.request("session:reconnect", { code: normalizeCode(code), token });
    this.rememberSession(result);
    return result;
  }

  async leave(): Promise<void> {
    const { code, token } = this.requireCredentials();
    await this.request("session:leave", { code, token });
    this.code = null;
    this.token = null;
    this.everJoined = false;
  }

  async setReady(ready: boolean): Promise<void> {
    const { code, token } = this.requireCredentials();
    await this.request("player:ready", { code, token, ready });
  }

  async sendMove(intent: ChessMoveIntent): Promise<ChessRemoteState> {
    const { code, token } = this.requireCredentials();
    const { state } = await this.request("game:intent", { code, token, intent });
    return state;
  }

  async resign(): Promise<void> {
    const { code, token } = this.requireCredentials();
    await this.request("game:resign", { code, token });
  }

  /** Ferme le socket sans quitter la session cote serveur (la fenetre de grace s'applique). */
  dispose(): void {
    this.listeners.clear();
    this.connectionListeners.clear();
    this.socket.removeAllListeners();
    this.socket.disconnect();
  }

  // --- Interne ----------------------------------------------------------------------

  private rememberSession(result: JoinedSessionResult): void {
    this.lastSession = result.session;
    this.code = result.session.code;
    if (result.token) {
      this.token = result.token;
      this.everJoined = true;
    }
  }

  private requireCredentials(): { code: string; token: string } {
    if (!this.code || !this.token) throw new OnlineError("Aucune session en cours.");
    return { code: this.code, token: this.token };
  }

  /**
   * Emet un evenement et attend son accuse de reception. Rejette avec une
   * `OnlineError` porteuse du message serveur si l'ack vaut `{ok:false}`.
   */
  private request<K extends keyof OnlineClientEvents>(
    event: K,
    payload: Parameters<OnlineClientEvents[K]>[0],
  ): Promise<AckPayload<K>> {
    return new Promise((resolve, reject) => {
      if (!this.socket.connected) {
        reject(new OnlineError("Non connecte au serveur."));
        return;
      }
      let settled = false;
      const timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new OnlineError("Le serveur ne repond pas."));
      }, ACK_TIMEOUT_MS);

      const ack = (result: AckResult<Record<string, unknown>>) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        if (!result || result.ok !== true) {
          reject(new OnlineError(result?.error ?? "Erreur inconnue du serveur."));
          return;
        }
        resolve(result as AckPayload<K>);
      };

      (this.socket.emit as (ev: string, p: unknown, a: typeof ack) => void)(event, payload, ack);
    });
  }
}

/** Type de la reponse (deja filtree sur `ok:true`) d'un evenement client -> serveur. */
type AckPayload<K extends keyof OnlineClientEvents> =
  Parameters<Parameters<OnlineClientEvents[K]>[1]>[0] extends infer R ? Extract<R, { ok: true }> : never;

/** Les codes de session sont a 6 caracteres, insensibles a la casse et aux espaces. */
export function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/\s+/g, "");
}
